'use client';

import Link from 'next/link';
import {
  Shield,
  Key,
  Webhook,
  Mail,
  CheckCircle,
  GitBranch,
  BookOpen,
  Monitor,
  DollarSign,
  FileJson,
  Link as LinkIcon,
  Lock,
  ShieldAlert,
  Globe,
  KeyRound,
  Bug,
  GitPullRequest,
  Building2,
  Cloud,
  Search,
  Box,
  type LucideIcon
} from 'lucide-react';
import type { BlogSeries } from '@/lib/data/blog-series';

// Map icon names to components
const iconMap: Record<string, LucideIcon> = {
  Shield,
  Key,
  Webhook,
  Mail,
  CheckCircle,
  GitBranch,
  BookOpen,
  Monitor,
  DollarSign,
  FileJson,
  Link: LinkIcon,
  Lock,
  ShieldAlert,
  Globe,
  KeyRound,
  Bug,
  GitPullRequest,
  Building2,
  Cloud,
  Search,
  Box
};

interface BlogSeriesCarouselProps {
  series: BlogSeries[];
}

export default function BlogSeriesCarousel({ series }: BlogSeriesCarouselProps) {
  return (
    <section className="bg-gray-50 border-b border-gray-200 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#2813e8]" />
            Featured Series
          </h2>
        </div>

        <div
          className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8
                     snap-x snap-mandatory scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent"
          style={{
            scrollbarWidth: 'thin',
            msOverflowStyle: 'none'
          }}
        >
          {series.map((s) => {
            const IconComponent = iconMap[s.icon] || BookOpen;

            return (
              <Link
                key={s.id}
                href={`/blog/${s.hubSlug}`}
                className="flex-shrink-0 w-44 sm:w-48 snap-start bg-white rounded-xl p-4
                           border border-gray-200 hover:border-[#2813e8]/30 hover:shadow-lg
                           transition-all duration-200 group"
              >
                <div
                  className={`w-10 h-10 rounded-lg bg-gradient-to-br ${s.color}
                              flex items-center justify-center mb-3 shadow-sm`}
                >
                  <IconComponent className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-semibold text-gray-900 group-hover:text-[#2813e8]
                              transition-colors line-clamp-2 mb-1 text-sm sm:text-base">
                  {s.name}
                </h3>
                <p className="text-xs sm:text-sm text-gray-500">
                  {s.articleCount} {s.articleCount === 1 ? 'guide' : 'guides'}
                </p>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
